import { useEffect, useRef, useState } from 'react';
import { useLocale } from '@/i18n';
import { notificationPreferencesApi, type NotificationPreferences } from '../api/notification-preferences.api';
import { NotificationSettings } from '../components/notification-settings';

export function NotificationSettingsContainer() {
  const { locale } = useLocale();
  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  const [saved, setSaved] = useState<NotificationPreferences | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<'load' | 'save' | null>(null);
  const [attempt, setAttempt] = useState(0);
  const request = useRef<AbortController | null>(null);
  const saving = useRef(false);
  useEffect(() => {
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError(null);
    void notificationPreferencesApi.load(controller.signal).then(value => {
      if (!controller.signal.aborted) { setDraft(value); setSaved(value); }
    }).catch(() => {
      if (!controller.signal.aborted) setError('load');
    }).finally(() => {
      if (!controller.signal.aborted) setBusy(false);
    });
    return () => { controller.abort(); request.current?.abort(); };
  }, [attempt]);

  const save = async () => {
    if (!draft || busy || saving.current) return;
    saving.current = true;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setError(null);
    try {
      const value = await notificationPreferencesApi.save(draft, controller.signal);
      if (!controller.signal.aborted) { setDraft(value); setSaved(value); }
    } catch {
      if (!controller.signal.aborted) setError('save');
    } finally {
      saving.current = false;
      if (!controller.signal.aborted) setBusy(false);
    }
  };
  return <NotificationSettings locale={locale} preferences={draft} busy={busy} error={error}
    dirty={JSON.stringify(draft) !== JSON.stringify(saved)}
    onChange={(key, value) => { setDraft(previous => previous && ({ ...previous, [key]: value })); setError(null); }}
    onSave={() => { void save(); }} onReload={() => setAttempt(value => value + 1)} />;
}
